import React, { FC, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';

import { CurrencyEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getDefaultSelectableTradingPLanFromState } from '../../lib/store/crm/helpers';
import { formatCurrencyBySymbol, getCurrencySymbolByCurrencyCode } from '../../lib/util/DataHelpers';

import IconGift from '../../assets/img/gift.svg';
import IconMedal from '../../assets/img/medal.svg';
import ClickableDiv from '../../components/ClickableDiv/ClickableDiv';
import TournamentEndTimerLite from '../../components/TournamentEndTimerLite/TournamentEndTimerLite';
import TournamentRulesModal from '../../components/TournamentRulesModal/TournamentRulesModal';
import config from '../../config';
import { useIsMobile } from '../../hooks';

import styles from './Tournament.module.scss';

type Props = {
  isTournamentStarted: boolean;
  isTournamentEnded: boolean;
} & ReduxProps;

const Tournament: FC<Props> = (props: Props): JSX.Element => {
  const isMobile = useIsMobile();
  const { tradingPlan, isTournamentStarted, isTournamentEnded } = props;
  const {
    name,
    id: tradingPlanId,
    plans_accounts_activation: date,
    current_prize_pool: currentPrizePool,
    final_prize_pool: finalPrizePool,
    prize_ccy: prizeCurrency,
    prize_winner_count: winnerCount,
    image_file_storage_id: imageId,
  } = tradingPlan;
  const [isDisplayTournamentRulesModal, setIsDisplayTournamentRulesModal] = useState<boolean>(false);
  const willTournamentStart = !isTournamentEnded && !isTournamentStarted;

  const openTournamentRulesPage = () => {
    window.open(config.links.tournamentRulesCapitalUrl, '_blank');
  };

  if (!tradingPlanId) {
    return <></>;
  }

  if (isTournamentEnded) {
    return (
      <div className={classNames(styles.wrapperEnded, { [styles.wrapperEndedMob]: isMobile })}>
        <div className={classNames(styles.card, {
          [styles.cardMobEnded]: isMobile,
          [styles.cardWebRow]: !isMobile,
        })}
        >
          <img
            alt=""
            className={classNames(styles.image, { [
            styles.imageEnded]: true,
            [styles.imageMob]: isMobile,
            })}
            src={`${config.cdn.url}/${imageId}`}
          />
          <div className={styles.titleAndDesc}>
            <span className={styles.title}>{lang.commonMyPositionTournamentEnded()}</span>
            <span className={styles.description}>{lang.commonMyPositionThanksForParticipating()}</span>
          </div>
        </div>
      </div>
    );
  }

  // started or will be started
  return (
    <div className={classNames(styles.wrapper, { [styles.wrapperEndedMob]: isMobile })}>
      <div
        className={classNames(styles.card, {
          [styles.cardMob]: isMobile,
          [styles.cardWillStarted]: !isMobile && !isTournamentStarted && !isTournamentEnded,
        })}
      >
        <img
          alt=""
          className={classNames(styles.image, {
            [styles.tournamentWillStartImg]: willTournamentStart && !isMobile,
            [styles.imageMob]: isMobile,
          })}
          src={`${config.cdn.url}/${imageId}`}
        />
        <div className={styles.summary}>
          <div className={classNames(styles.row, { [styles.rowMob]: isMobile })}>
            <span className={styles.title}>{name}</span>
          </div>
          <div className={classNames(styles.row, { [styles.rowMob]: isMobile })}>
            <TournamentEndTimerLite />
          </div>
          <div className={classNames(styles.row, { [styles.rowMob]: isMobile })}>
            <div className={classNames(styles.iconAndLabel, { [styles.iconAndLabelMob]: isMobile })}>
              <img alt="" className={styles.clock} src={IconGift} />
              <span className={styles.date}>
                {lang.commonMyPositionPrizeFund(
                  formatCurrencyBySymbol(
                    finalPrizePool ?? currentPrizePool,
                    getCurrencySymbolByCurrencyCode(CurrencyEnum.BGN),
                    false,
                    true,
                  ),
                )}
              </span>
            </div>
            <div className={classNames(styles.iconAndLabel, { [styles.iconAndLabelMob]: isMobile })}>
              <img alt="" className={styles.clock} src={IconMedal} />
              <span className={styles.date}>{lang.commonMyPositionPrizes(winnerCount)}</span>
            </div>

            <ClickableDiv
              className={classNames(styles.displayTournamentBtn, { [styles.displayTournamentBtnMob]: isMobile })}
              onClick={() => openTournamentRulesPage()}
            >
              {lang.commonMyPositionSeeRules()}
            </ClickableDiv>
          </div>
        </div>
      </div>
      <TournamentRulesModal
        isDisplayTournamentRulesModal={isDisplayTournamentRulesModal}
        setIsDisplayTournamentRulesModal={setIsDisplayTournamentRulesModal}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  tradingPlan: getDefaultSelectableTradingPLanFromState(state.crm),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Tournament);
